import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  ACTION_LABEL,
  ACTION_STATUSES,
  DECISION_LABEL,
  DECISION_STATUSES,
  formatDue,
  nameToSave,
  priorityLabel,
  describeSendError,
  pushFailureText,
  visibleName,
  type SendDestination,
} from '../lib/ledgerCopy.js';
import type { ActionStatus, Decision, DecisionStatus, ExternalRef, MeetingAction } from '../lib/api.js';
import { formatMsClock } from '../lib/format.js';

export interface SendChoices {
  basecampConfigured: boolean;
  basecampConnected: boolean;
  basecampReady: boolean;
  usual: SendDestination;
  emailLabel: string;
}

function EvidenceButton({ ms, seek }: { ms: number | null; seek: (ms: number) => void }): JSX.Element | null {
  if (typeof ms !== 'number') return null;
  return (
    <button type="button" className="summary-topic-time ledger-seek" onClick={() => seek(ms)}>
      {formatMsClock(ms)}
    </button>
  );
}

function MoreMenu({ children }: { children: ReactNode }): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointer(event: MouseEvent): void {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="note-more" ref={ref}>
      <button
        type="button"
        className="btn btn--tertiary btn--sm"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((value) => !value)}
      >
        More
      </button>
      {open && (
        <div className="note-menu" role="menu" onClick={() => setOpen(false)}>
          {children}
        </div>
      )}
    </div>
  );
}

function NoteEditor({
  text,
  onText,
  name,
  onName,
  nameLabel,
  namePlaceholder,
  statusValue,
  onStatus,
  statusOptions,
  dirty,
  busy,
  onCancel,
  onSave,
}: {
  text: string;
  onText: (value: string) => void;
  name: string;
  onName: (value: string) => void;
  nameLabel: string;
  namePlaceholder: string;
  statusValue: string;
  onStatus: (value: string) => void;
  statusOptions: Array<{ value: string; label: string }>;
  dirty: boolean;
  busy: boolean;
  onCancel: () => void;
  onSave: () => void;
}): JSX.Element {
  return (
    <div className="note-edit">
      <textarea value={text} onChange={(e) => onText(e.target.value)} aria-label="Note" rows={3} />
      <select value={statusValue} aria-label="Status" onChange={(e) => onStatus(e.target.value)}>
        {statusOptions.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
      <input
        value={name}
        onChange={(e) => onName(e.target.value)}
        placeholder={namePlaceholder}
        aria-label={nameLabel}
      />
      <div className="note-edit__actions">
        <button type="button" className="btn btn--tertiary btn--sm" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        {dirty && (
          <button type="button" className="btn btn--sm" disabled={busy || !text.trim()} onClick={onSave}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>
    </div>
  );
}

export function DecisionCard({
  row,
  seek,
  onSave,
}: {
  row: Decision;
  seek: (ms: number) => void;
  onSave: (row: Decision, patch: Partial<Pick<Decision, 'text' | 'decidedBy' | 'status'>>) => Promise<void>;
}): JSX.Element {
  const [text, setText] = useState(row.text);
  const [owner, setOwner] = useState(visibleName(row.decidedBy));
  const [status, setStatus] = useState<DecisionStatus>(row.status);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const dirty = text.trim() !== row.text || owner.trim() !== visibleName(row.decidedBy) || status !== row.status;

  useEffect(() => {
    setText(row.text);
    setOwner(visibleName(row.decidedBy));
    setStatus(row.status);
  }, [row.text, row.decidedBy, row.status, row.updatedAt]);

  function cancel(): void {
    setText(row.text);
    setOwner(visibleName(row.decidedBy));
    setStatus(row.status);
    setEditing(false);
  }

  const who = visibleName(row.decidedBy);

  return (
    <li className="note-card">
      {editing ? (
        <NoteEditor
          text={text}
          onText={setText}
          name={owner}
          onName={setOwner}
          nameLabel="Who decided this"
          namePlaceholder="Name, if you know it"
          statusValue={status}
          onStatus={(value) => setStatus(value as DecisionStatus)}
          statusOptions={DECISION_STATUSES.map((value) => ({ value, label: DECISION_LABEL[value] }))}
          dirty={dirty}
          busy={busy}
          onCancel={cancel}
          onSave={() => {
            setBusy(true);
            void onSave(row, {
              text: text.trim(),
              decidedBy: nameToSave(owner, row.decidedBy),
              status,
            })
              .then(() => setEditing(false))
              .finally(() => setBusy(false));
          }}
        />
      ) : (
        <div className="note-card__read">
          <div className="note-card__main">
            <div className="note-card__line">
              <EvidenceButton ms={row.evidenceStartMs} seek={seek} />
              <span className="note-card__text">{row.text}</span>
            </div>
            <div className="note-card__meta">
              <span className="note-status">{DECISION_LABEL[row.status]}</span>
              {who && <span>{who}</span>}
              {row.source === 'manual' && <span>Edited</span>}
            </div>
          </div>
          <button type="button" className="btn btn--tertiary btn--sm" onClick={() => setEditing(true)}>
            Edit
          </button>
        </div>
      )}
    </li>
  );
}

function basecampSetupLink(send: SendChoices): JSX.Element | null {
  if (!send.basecampConfigured || send.basecampReady) return null;
  const label = send.basecampConnected ? 'Add a Basecamp project' : 'Connect Basecamp';
  return <a role="menuitem" href="/settings/integrations#basecamp">{label}</a>;
}

export function ActionCard({
  row,
  seek,
  send,
  onSave,
  onPush,
}: {
  row: MeetingAction;
  seek: (ms: number) => void;
  send: SendChoices;
  onSave: (row: MeetingAction, patch: Partial<Pick<MeetingAction, 'text' | 'owner' | 'status'>>) => Promise<void>;
  onPush: (destination: SendDestination) => Promise<void>;
}): JSX.Element {
  const [text, setText] = useState(row.text);
  const [owner, setOwner] = useState(visibleName(row.owner));
  const [status, setStatus] = useState<ActionStatus>(row.status);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);
  const dirty = text.trim() !== row.text || owner.trim() !== visibleName(row.owner) || status !== row.status;
  const links = (row.externalRefs ?? []).filter((ref): ref is ExternalRef & { url: string } => !!ref.url);
  const who = visibleName(row.owner);
  const priority = priorityLabel(row.priority);
  const emailed = row.lastPush?.status === 'succeeded' && row.lastPush.destination === 'email';

  useEffect(() => {
    setText(row.text);
    setOwner(visibleName(row.owner));
    setStatus(row.status);
  }, [row.text, row.owner, row.status, row.updatedAt]);

  function cancel(): void {
    setText(row.text);
    setOwner(visibleName(row.owner));
    setStatus(row.status);
    setEditing(false);
  }

  function push(destination: SendDestination): void {
    setBusy(true);
    setPushError(null);
    void onPush(destination)
      .catch((err) => setPushError(describeSendError(err)))
      .finally(() => setBusy(false));
  }

  const emailItem = (
    <button type="button" role="menuitem" onClick={() => push('email')}>
      {send.emailLabel}
    </button>
  );
  const basecampItem = send.basecampReady ? (
    <button type="button" role="menuitem" onClick={() => push('basecamp')}>Send to Basecamp</button>
  ) : (
    basecampSetupLink(send)
  );

  const cardClass = [
    'note-card',
    row.status === 'done' ? 'note-card--done' : '',
    row.status === 'cancelled' ? 'note-card--cancelled' : '',
  ].filter(Boolean).join(' ');

  return (
    <li className={cardClass}>
      {editing ? (
        <NoteEditor
          text={text}
          onText={setText}
          name={owner}
          onName={setOwner}
          nameLabel="Who is doing this"
          namePlaceholder="Who is doing this?"
          statusValue={status}
          onStatus={(value) => setStatus(value as ActionStatus)}
          statusOptions={ACTION_STATUSES.map((value) => ({ value, label: ACTION_LABEL[value] }))}
          dirty={dirty}
          busy={busy}
          onCancel={cancel}
          onSave={() => {
            setBusy(true);
            void onSave(row, {
              text: text.trim(),
              owner: nameToSave(owner, row.owner),
              status,
            })
              .then(() => setEditing(false))
              .finally(() => setBusy(false));
          }}
        />
      ) : (
        <div className="note-card__read">
          <input
            type="checkbox"
            className="note-check"
            checked={row.status === 'done'}
            disabled={busy}
            aria-label={row.status === 'done' ? 'Mark to do' : 'Mark done'}
            onChange={() => {
              setBusy(true);
              void onSave(row, { status: row.status === 'done' ? 'open' : 'done' }).finally(() => setBusy(false));
            }}
          />
          <div className="note-card__main">
            <div className="note-card__line">
              <EvidenceButton ms={row.evidenceStartMs} seek={seek} />
              <span className="note-card__text">{row.text}</span>
            </div>
            <div className="note-card__meta">
              <span className="note-status">{ACTION_LABEL[row.status]}</span>
              {who && <span>{who}</span>}
              {priority && <span>{priority}</span>}
              {row.dueDate && <span>{formatDue(row.dueDate)}</span>}
              {links.map((ref) => (
                <a key={ref.provider} href={ref.url} target="_blank" rel="noreferrer">
                  {ref.provider === 'basecamp' ? 'In Basecamp' : ref.provider}
                </a>
              ))}
              {emailed && <span>Emailed</span>}
              {row.source === 'manual' && <span>Edited</span>}
            </div>
          </div>
          <MoreMenu>
            <button type="button" role="menuitem" onClick={() => setEditing(true)}>Edit</button>
            {send.usual === 'basecamp' ? (
              <>
                {basecampItem}
                {emailItem}
              </>
            ) : (
              <>
                {emailItem}
                {basecampItem}
              </>
            )}
          </MoreMenu>
        </div>
      )}
      {pushError && <div className="form-error" style={{ marginTop: 8 }}>{pushError}</div>}
      {!pushError && row.lastPush?.status === 'failed' && row.lastPush.lastError && (
        <div className="form-error" style={{ marginTop: 8 }}>{pushFailureText(row.lastPush.lastError)}</div>
      )}
    </li>
  );
}
